import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import { useCallback } from 'react';
import { SessionParticipant } from '../../types/videocall.types';

interface UseStartMeetingProps {
  meetingContainerRef: React.RefObject<HTMLDivElement | null>;
  initializeZegoCloud: (participant: SessionParticipant) => Promise<ZegoUIKitPrebuilt>;
  redirectToDashboard: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  setMeetingStarted: (started: boolean) => void;
  setZegoInstance:  (instance: ZegoUIKitPrebuilt | null) => void;
  meetingStarted: boolean;
}


const CONTAINER_CHECK_INTERVAL = 500;

export const useStartMeeting = ({
  meetingContainerRef,
  initializeZegoCloud,
  redirectToDashboard,
  setError,
  setLoading,
  setMeetingStarted,
  setZegoInstance,
  meetingStarted,
}: UseStartMeetingProps) => {
  return useCallback(
    async (participant: SessionParticipant) => {
      try {
        if (!meetingContainerRef.current) {
          console.log('Waiting for container to be available...');
          await new Promise<void>((resolve) => {
            const checkContainer = () => {
              if (meetingContainerRef.current) {
                resolve();
              } else {
                setTimeout(checkContainer, CONTAINER_CHECK_INTERVAL);
              }
            };
            checkContainer();
          });
        }

        // Double-check after waiting
        if (!meetingContainerRef.current) {
          throw new Error('Meeting container is not available');
        }

        if (!participant || meetingStarted) {
          throw new Error('Invalid meeting state');
        }

        const zp = await initializeZegoCloud(participant);
        setZegoInstance(zp);

        // Style the container explicitly
        const container = meetingContainerRef.current;
        container.style.width = '100%';
        container.style.height = '100vh';

        // Join the room with proper configuration
        await zp.joinRoom({
          container,
          sharedLinks: [{ name: 'Meeting Link', url: window.location.href }],
          scenario: {
            mode: ZegoUIKitPrebuilt.OneONoneCall,
          },
          showScreenSharingButton: true,
          turnOnCameraWhenJoining: false,
          turnOnMicrophoneWhenJoining: true,
          showLeavingView: false,
          onJoinRoom: () => {
            console.log('Successfully joined room');
            setMeetingStarted(true);
          },
          onLeaveRoom: () => {
            console.log('Left room, redirecting to dashboard');
            redirectToDashboard();
          },
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('❌ Error starting meeting:', error);
        setError(`Failed to start meeting: ${errorMessage}`);
        setLoading(false);
      }
    },
    [
      initializeZegoCloud,
      meetingContainerRef,
      meetingStarted,
      redirectToDashboard,
      setError,
      setLoading,
      setMeetingStarted,
      setZegoInstance,
    ]
  );
};

export default useStartMeeting;